import React, { useState, useContext } from 'react';
import { EmptyData } from '../../../../shared/components/EmptyData';
import { TicketTaskCard } from './TicketTaskCard';
import EmptySVG from '../../../../../svg/empty.svg';
import { platformService } from '../../../../shared/services/platform.service';
import { TicketTasksListSkeleton } from '../../../../shared/components/TicketTasks/Skeletons/TicketTasksSkeleton';
import { getUser } from '../../../../AuthConfig';
import { useHistory, useLocation } from 'react-router-dom';
import { FEATURE_NOT_AVAILABLE, LICENSE_FEATURE, MyWorkListViewType, STATUS_GUID, TASK_STATUS_GUID } from '../../../../shared/utils/constants';
import { getCachedFeature } from '../../../../shared/cache/FeatureCache';
import { getShowClosedFromStorage } from '../../../MyWork/MyWorkList/myWorkListHelper';
import { MyWorkContext } from '../../../MyWork/MyWorkContextProvider';
import { Route } from '../../../../../route-constants';
import { useSetState } from '../../../../AppState';
import { UpgradeNeeded } from '../../../../shared/components/UpgradeNeeded';
import { useTranslation } from 'react-i18next';
import { currentPhaseFilter } from '../../../../shared/utils/lifecyclePhaseFilter';
import { useLanePaging } from '../../../MyWork/MyWorkBoard/useLanePaging';
import { LaneFrame, VirtualLane } from '../../../MyWork/MyWorkBoard/VirtualLane';

export const TicketTasksTemplate = () => {
  const api = new platformService();
  const history = useHistory();
  const userName: string = getUser().toLowerCase();
  const [isFeatureAvailable, setIsFeatureAvailable] = useState<boolean>(false);
  const { myWorkStates } = useContext(MyWorkContext);
  const location = useLocation();
  const { t } = useTranslation();

  const isMyGroupWork = location.pathname.includes(Route.myGroupWork.pagePath);
  const isMyWork = location.pathname.includes(Route.myWork.pagePath);

  const isClosed = getShowClosedFromStorage(isMyGroupWork) ?? myWorkStates.showClosed;
  const setAppState = useSetState();

  // A page at a time, as the ticket board's lanes load; Show Closed starts the lane over.
  const fetchPage = async (skip: number, top: number) => {
    const [isTicketTaskEnabled, isLifecycleEnabled] = await Promise.all([
      getCachedFeature(LICENSE_FEATURE.TicketTasks),
      getCachedFeature(LICENSE_FEATURE.TicketLifecycle)
    ]);
    setIsFeatureAvailable(isTicketTaskEnabled);
    if (!isTicketTaskEnabled) return [];
    const phaseFilter = currentPhaseFilter('', isLifecycleEnabled);
    const taskFilter = isClosed ?
      `(Ticket/Closed eq true or Ticket/Status/Guid eq ${STATUS_GUID.RESOLVED} or Status/Guid eq ${TASK_STATUS_GUID.COMPLETED} or Status/Guid eq ${TASK_STATUS_GUID.FAILED} or Status/Guid eq ${TASK_STATUS_GUID.SKIPPED})` :
      `(Ticket/Closed eq false and Ticket/Status/Guid ne ${STATUS_GUID.RESOLVED} and Status/Guid ne ${TASK_STATUS_GUID.COMPLETED} and Status/Guid ne ${TASK_STATUS_GUID.FAILED} and Status/Guid ne ${TASK_STATUS_GUID.SKIPPED})`;
    const params: string = `?$filter=(Assignee/UserName eq '${userName}' or TaskCollaborators/any(tc: tc/PlatformUser/UserName eq '${userName}'))  and ${taskFilter} and ${phaseFilter} &$orderby=CreatedDate desc&$expand=Status,Assignee,Team,SupportGroup,TaskCollaborators,Ticket($select=Title,Id)&$top=${top}&$skip=${skip}`;
    return api.getTicketTasks(params) as Promise<TicketTask[]>;
  };
  const { items: ticketTasksList, hasMore, isLoadingMore, loadMore } = useLanePaging(fetchPage, task => task.Id, `${isClosed}|${userName}`);

  const onTaskClick = (ticketId: number, taskId: number) => {
    setAppState((prev) => ({
      ...prev,
      isRouteFromMyWork: (isMyGroupWork || isMyWork),
      selectedTaskOrApproval: { type: MyWorkListViewType.Tasks, id: taskId },
    }));
    const route = isMyGroupWork ? Route.myGroupWork.editTicket : Route.myWork.editTicket;
    const path = `${route}/${ticketId}`;
    const from = isMyGroupWork ? 'mygroupwork' : 'mywork';
    history.push(path, { from });
  }

  const taskCard = (ticketTask: TicketTask) => (
    <div className={`mb-2 cursor-pointer`} title={`${t('my-work.task-template.ticket-title')}: ${ticketTask.TicketId}`} onClick={() => { onTaskClick(ticketTask.TicketId, ticketTask.Id) }}>
      <TicketTaskCard ticketTask={ticketTask} />
    </div>
  );

  return (
    <>
      {!ticketTasksList ? <LaneFrame><TicketTasksListSkeleton /></LaneFrame> : <>
        {isFeatureAvailable && (ticketTasksList.length > 0 ? (
          <VirtualLane
            items={ticketTasksList}
            keyOf={task => task.Id}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
            loadingRows={[<TicketTasksListSkeleton key='a' />]}
            renderItem={taskCard}
          />
        ) : (
          <LaneFrame>
            <EmptyData headerText={t('my-work.my-ticket.no-task')} SVGIcon={<EmptySVG width={75} height={75} />} />
          </LaneFrame>
        ))}
        {!isFeatureAvailable && <LaneFrame><div className='flex flex-col justify-around h-full w-full'>
          <UpgradeNeeded
            headerText={FEATURE_NOT_AVAILABLE.TaskHeader}
            subheaderText={FEATURE_NOT_AVAILABLE.TaskSubHeader}
          />
        </div></LaneFrame>}
      </>}
    </>
  );
};
